import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import './ServerErrorOverlay.css';

interface ServerErrorState {
  visible: boolean;
  traceCode: string | null;
}

interface ServerErrorContextValue {
  show: (traceCode?: string | null) => void;
}

const ServerErrorContext = createContext<ServerErrorContextValue | null>(null);

export function ServerErrorProvider({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  const [state, setState] = useState<ServerErrorState>({ visible: false, traceCode: null });

  const show = useCallback((traceCode?: string | null) => {
    setState({ visible: true, traceCode: traceCode || null });
  }, []);

  const handleClose = useCallback(() => {
    setState({ visible: false, traceCode: null });
  }, []);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ServerErrorContext.Provider value={api}>
      {children}
      {state.visible && (
        <div className="server-error-overlay" role="alertdialog" aria-modal="true" aria-labelledby="server-error-title">
          <div className="server-error-overlay__backdrop" />
          <div className="server-error-overlay__content">
            <div className="server-error-overlay__icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 id="server-error-title" className="server-error-overlay__title">{t('serverError.title')}</h2>
            <p className="server-error-overlay__description">{t('serverError.description')}</p>
            {state.traceCode && (
              <code className="server-error-overlay__trace">{t('serverError.traceCode', { code: state.traceCode })}</code>
            )}
            <div className="server-error-overlay__actions">
              <button className="server-error-overlay__btn server-error-overlay__btn--primary" onClick={handleRefresh}>
                {t('serverError.refreshPage')}
              </button>
              <button className="server-error-overlay__btn server-error-overlay__btn--secondary" onClick={handleClose}>
                {t('serverError.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ServerErrorContext.Provider>
  );
}

export function useServerError(): ServerErrorContextValue {
  const context = useContext(ServerErrorContext);
  if (!context) {
    throw new Error('useServerError must be used within ServerErrorProvider');
  }
  return context;
}

// Singleton reference for use outside React (e.g., in http.ts)
let globalShowError: ((traceCode?: string | null) => void) | null = null;

export function registerGlobalServerError(show: (traceCode?: string | null) => void) {
  globalShowError = show;
}

export function triggerServerError(traceCode?: string | null) {
  if (globalShowError) {
    globalShowError(traceCode);
  }
}
